import { API_URL } from '@/constants/env'

import type { OrganizationMCPServer } from '@/types/MCPServer'

/**
 * Generate a random code verifier for PKCE
 */
function generateCodeVerifier(): string {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

/**
 * Generate code challenge from verifier for PKCE
 */
async function generateCodeChallenge(verifier: string): Promise<string> {
  const encoder = new TextEncoder()
  const data = encoder.encode(verifier)
  const digest = await crypto.subtle.digest('SHA-256', data)
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=/g, '')
}

export interface OAuthMetadata {
  issuer: string
  authorization_endpoint: string
  token_endpoint: string
  registration_endpoint?: string
  revocation_endpoint?: string
  token_endpoint_auth_methods_supported?: string[]
  response_types_supported?: string[]
  grant_types_supported?: string[]
}

export interface ClientRegistration {
  client_id: string
  client_secret: string
  redirect_uri: string
  client_name: string
  grant_types: string[]
  response_types: string[]
  token_endpoint_auth_method: string
}

export interface TokenResponse {
  access_token: string
  id?: string
  instance_url?: string
  issued_at?: string
  signature?: string
  token_type: string
  expires_in?: number
  refresh_token?: string
  scope?: string
}

export interface TokenMessage {
  access_token: string
  refresh_token?: string
  token_expiry?: number
  client_id: string
  client_secret: string
  provider_metadata?: Record<string, string>
}

interface MicrosoftAdminConsentMessage {
  adminConsent: boolean
  error?: string
  errorDescription?: string
  scope?: string | null
  state?: string | null
  tenant?: string | null
  type: 'microsoft_admin_consent_callback'
}

interface OAuthAuthorizeRequest {
  response_type: 'code'
  client_id: string
  redirect_uri: string
  state: string
  scope: string
  code_challenge: string
  code_challenge_method: string
  prompt?: string
}

interface OAuthTokenRequest {
  grant_type: 'authorization_code'
  code: string
  redirect_uri: string
  client_id: string
  client_secret: string
  code_verifier?: string
}

const toUrlSearchParams = (values: object): URLSearchParams => {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(values)) {
    if (typeof value === 'string') {
      params.append(key, value)
    }
  }
  return params
}

export interface DCRRegistrationStatus {
  server_id: string
  server_name: string
  has_active_registration: boolean
  client_id?: string
  client_secret?: string
  client_secret_expires_at?: string
  needs_reregistration: boolean
  reason?: string
}

/**
 * Check if an active DCR registration exists for the MCP server
 */
async function checkDCRRegistration(
  serverId: string,
  propelAuthToken: string,
  orgId?: string,
): Promise<DCRRegistrationStatus> {
  const headers: Record<string, string> = {
    Authorization: `Bearer ${propelAuthToken}`,
  }

  if (orgId) {
    headers['X-Organization-Id'] = orgId
  }

  const response = await fetch(`${API_URL}/api/v1/mcp-dcr/${serverId}/check`, {
    method: 'GET',
    headers,
  })

  if (!response.ok) {
    throw new Error(`Failed to check DCR registration: ${response.statusText}`)
  }

  return response.json()
}

/**
 * Discover OAuth metadata from the MCP server
 */
export const getOAuthServerBaseUrl = (serverUrl: string): string =>
  serverUrl
    .replace(/\/(sse|mcp|streamable-http|stdio)\/?$/, '')
    .replace(/\/$/, '')

const base64UrlEncode = (value: string): string =>
  btoa(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')

const COMMERCIAL_MICROSOFT_OAUTH_HOSTS = {
  graphHost: 'graph.microsoft.com',
  loginHost: 'login.microsoftonline.com',
} as const

const GCC_HIGH_MICROSOFT_OAUTH_HOSTS = {
  graphHost: 'graph.microsoft.us',
  loginHost: 'login.microsoftonline.us',
} as const

type MicrosoftOAuthProviderConfig =
  | OrganizationMCPServer['oauth_provider_config']
  | Record<string, unknown>
  | null
  | undefined

export const resolveMicrosoftOAuthHosts = (
  providerConfig: MicrosoftOAuthProviderConfig,
): {
  graphHost: string
  loginHost: string
} => {
  const loginHost =
    typeof providerConfig?.login_host === 'string' &&
    providerConfig.login_host.length > 0
      ? providerConfig.login_host
      : null
  const graphHost =
    typeof providerConfig?.graph_host === 'string' &&
    providerConfig.graph_host.length > 0
      ? providerConfig.graph_host
      : null
  const nationalCloud =
    typeof providerConfig?.national_cloud === 'string'
      ? providerConfig.national_cloud
      : 'commercial'
  const fallbackHosts =
    nationalCloud === 'gcc_high'
      ? GCC_HIGH_MICROSOFT_OAUTH_HOSTS
      : COMMERCIAL_MICROSOFT_OAUTH_HOSTS

  return {
    graphHost: graphHost ?? fallbackHosts.graphHost,
    loginHost: loginHost ?? fallbackHosts.loginHost,
  }
}

const toMicrosoftScopeValue = (
  permissionLabel: string,
  graphHost: string,
): string => {
  if (
    permissionLabel.includes('://') ||
    permissionLabel === 'offline_access' ||
    permissionLabel === 'openid' ||
    permissionLabel === 'profile' ||
    permissionLabel === 'email'
  ) {
    return permissionLabel
  }

  return `https://${graphHost}/${permissionLabel}`
}

export const buildMicrosoftAdminConsentUrl = ({
  clientId,
  loginHost,
  permissionLabels,
  redirectUri,
  state,
  tenant,
  graphHost,
}: {
  clientId: string
  loginHost: string
  permissionLabels: string[]
  redirectUri: string
  state: string
  tenant: string
  graphHost: string
}): string => {
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: permissionLabels
      .map((permissionLabel) =>
        toMicrosoftScopeValue(permissionLabel, graphHost),
      )
      .join(' '),
    state,
  })

  return `https://${loginHost}/${encodeURIComponent(tenant)}/v2.0/adminconsent?${params.toString()}`
}

async function discoverOAuthMetadata(
  serverUrl: string,
  serverId?: string,
  authType?: string | null,
  orgId?: string,
  propelAuthToken?: string,
): Promise<OAuthMetadata> {
  if (authType === 'mitm' && serverId && orgId && propelAuthToken) {
    const response = await fetch(
      `${API_URL}/api/v1/mcp-oauth/${serverId}/metadata?organization_id=${encodeURIComponent(orgId)}`,
      {
        headers: {
          Authorization: `Bearer ${propelAuthToken}`,
        },
      },
    )
    if (!response.ok) {
      throw new Error(
        `Failed to discover OAuth metadata: ${response.statusText}`,
      )
    }
    return response.json()
  }

  // Remove trailing SSE/MCP suffix (with or without trailing slash) so discovery hits the canonical host
  const baseUrl = getOAuthServerBaseUrl(serverUrl)

  const discoveryUrl = `${baseUrl}/.well-known/oauth-authorization-server`

  const response = await fetch(discoveryUrl)
  if (!response.ok) {
    throw new Error(`Failed to discover OAuth metadata: ${response.statusText}`)
  }

  return response.json()
}

export async function requestMicrosoftAdminConsent(
  server: OrganizationMCPServer,
  permissionLabels: string[],
): Promise<void> {
  const clientId =
    server.oauth_client_id ?? server.mcp_server.managed_client_id ?? null
  if (!clientId) {
    throw new Error('No Microsoft client ID is configured.')
  }

  const { graphHost, loginHost } = resolveMicrosoftOAuthHosts(
    server.oauth_provider_config,
  )
  const tenant =
    (server.oauth_provider_config?.tenant_id as string) || 'organizations'
  const callbackUrl = `${getOAuthServerBaseUrl(server.mcp_server.server_url)}/callback`
  const state = base64UrlEncode(
    JSON.stringify({
      nonce: crypto.randomUUID(),
      target_origin: window.location.origin,
    }),
  )
  const authUrl = buildMicrosoftAdminConsentUrl({
    clientId,
    graphHost,
    loginHost,
    permissionLabels,
    redirectUri: callbackUrl,
    state,
    tenant,
  })
  const popup = window.open(
    authUrl,
    'microsoft-admin-consent',
    'width=600,height=700,left=200,top=100',
  )

  if (!popup) {
    throw new Error(
      'Failed to open admin consent window. Please check your popup blocker.',
    )
  }

  const expectedOrigin = new URL(callbackUrl).origin

  return new Promise((resolve, reject) => {
    const cleanup = () => {
      window.removeEventListener('message', handleMessage)
      clearInterval(checkClosed)
    }

    const handleMessage = (
      event: MessageEvent<MicrosoftAdminConsentMessage>,
    ) => {
      if (event.origin !== expectedOrigin) {
        return
      }

      if (event.data?.type !== 'microsoft_admin_consent_callback') {
        return
      }

      if (event.data.state !== state) {
        return
      }

      cleanup()

      if (event.data.adminConsent) {
        resolve()
        return
      }

      reject(
        new Error(
          event.data.errorDescription ||
            event.data.error ||
            'Microsoft admin consent was not granted.',
        ),
      )
    }

    window.addEventListener('message', handleMessage)

    const checkClosed = window.setInterval(() => {
      if (popup.closed) {
        cleanup()
        reject(new Error('Admin consent window was closed'))
      }
    }, 1000)
  })
}

/**
 * Register a new OAuth client dynamically
 */
async function registerClient(
  registrationEndpoint: string,
  serverName: string,
  serverId: string,
  authType?: string,
  orgId?: string,
  userId?: string,
  propelAuthToken?: string,
): Promise<ClientRegistration> {
  const clientName = `Sweetspot Chat Client - ${crypto.randomUUID()}`
  const redirectUri = `${window.location.origin}/oauth/callback`
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  }

  let endpoint = registrationEndpoint
  if (authType === 'mitm' && propelAuthToken) {
    headers.Authorization = `Bearer ${propelAuthToken}`
    endpoint = `${API_URL}/api/v1/mcp-oauth/${serverId}/register?organization_id=${encodeURIComponent(orgId || '')}`
  } else if (authType === 'mitm') {
    const params = new URLSearchParams()
    if (orgId) {
      params.append('organization_id', orgId)
    }
    if (userId) {
      params.append('user_id', userId)
    }
    if (params.toString()) {
      endpoint = `${registrationEndpoint}?${params.toString()}`
    }
  }

  const response = await fetch(endpoint, {
    method: 'POST',
    headers,
    body: JSON.stringify({
      client_name: clientName,
      redirect_uris: [redirectUri],
      grant_types: ['authorization_code', 'refresh_token'],
      response_types: ['code'],
      token_endpoint_auth_method: 'client_secret_post',
      scope: 'openid profile email',
    }),
  })

  if (!response.ok) {
    const errorText = await response.text()
    throw new Error(`Failed to register OAuth client: ${errorText}`)
  }

  const registration = await response.json()

  // Ensure redirect_uri is included in the registration object
  // The server might return redirect_uris (plural) instead of redirect_uri
  const completeRegistration = {
    ...registration,
    redirect_uri: registration.redirect_uris?.[0] || redirectUri,
  }

  // Store in sessionStorage for reuse during this session
  sessionStorage.setItem(
    `oauth_client_${serverName}`,
    JSON.stringify(completeRegistration),
  )

  return completeRegistration
}

/**
 * Get or create client registration
 */
async function getClientRegistration(
  metadata: OAuthMetadata,
  serverName: string,
  serverId: string,
  authType?: string,
  orgId?: string,
  userId?: string,
  propelAuthToken?: string,
): Promise<ClientRegistration> {
  // For MITM servers, check for existing DCR registration first
  if (authType === 'mitm' && propelAuthToken) {
    try {
      const dcrStatus = await checkDCRRegistration(
        serverId,
        propelAuthToken,
        orgId,
      )

      if (
        dcrStatus.has_active_registration &&
        dcrStatus.client_id &&
        dcrStatus.client_secret
      ) {
        // We have a valid registration with credentials - reuse it
        console.log(`[DCR] Reusing existing registration for ${serverName}`)

        const redirectUri = `${window.location.origin}/oauth/callback`
        const registration: ClientRegistration = {
          client_id: dcrStatus.client_id,
          client_secret: dcrStatus.client_secret,
          redirect_uri: redirectUri,
          client_name: `Sweetspot Chat Client - Reused`,
          grant_types: ['authorization_code', 'refresh_token'],
          response_types: ['code'],
          token_endpoint_auth_method: 'client_secret_post',
        }

        // Store in session for this session
        sessionStorage.setItem(
          `oauth_client_${serverName}`,
          JSON.stringify(registration),
        )

        return registration
      } else if (dcrStatus.needs_reregistration) {
        console.log(
          `[DCR] Re-registration needed for ${serverName}: ${dcrStatus.reason}`,
        )
        // Fall through to register new client
      }
    } catch (error: any) {
      // Check for specific error conditions
      if (
        error.message?.includes('401') ||
        error.message?.includes('Unauthorized')
      ) {
        console.error(`[DCR] Authentication failed - auth token may be expired`)
        throw new Error(
          'Authentication failed. Please refresh the page and try again.',
        )
      }
      console.error(`[DCR] Failed to check registration status:`, error)
      // Fall through to register new client
    }

    // Need to register a new client
    return registerClient(
      metadata.registration_endpoint!,
      serverName,
      serverId,
      authType,
      orgId,
      userId,
      propelAuthToken,
    )
  }

  // For non-MITM servers, allow reuse within the session
  const storedRegistration = sessionStorage.getItem(
    `oauth_client_${serverName}`,
  )
  if (storedRegistration) return JSON.parse(storedRegistration)

  // If dynamic registration is supported, register a new client
  if (metadata.registration_endpoint) {
    return registerClient(
      metadata.registration_endpoint,
      serverName,
      serverId,
      authType,
      orgId,
      userId,
      propelAuthToken,
    )
  }

  // Otherwise, use a default client configuration
  // This would typically be pre-configured on the server
  throw new Error(
    'Dynamic client registration not supported and no pre-configured client available',
  )
}

/**
 * Exchange authorization code for tokens
 */
async function exchangeCodeForTokens(
  tokenEndpoint: string,
  code: string,
  registration: ClientRegistration,
  redirectUri: string,
  codeVerifier?: string,
  organizationId?: string,
  userId?: string,
  serverId?: string,
  authType?: string | null,
  propelAuthToken?: string,
): Promise<TokenResponse> {
  const tokenRequest: OAuthTokenRequest = {
    grant_type: 'authorization_code',
    code,
    redirect_uri: redirectUri,
    client_id: registration.client_id,
    client_secret: registration.client_secret,
  }

  if (codeVerifier) {
    tokenRequest.code_verifier = codeVerifier
  }

  // Build the URL with query parameters for MITM servers
  let url = tokenEndpoint
  const headers: Record<string, string> = {
    'Content-Type': 'application/x-www-form-urlencoded',
  }
  let body = toUrlSearchParams(tokenRequest).toString()
  if (authType === 'mitm' && serverId && organizationId && propelAuthToken) {
    url = `${API_URL}/api/v1/mcp-oauth/${serverId}/token?organization_id=${encodeURIComponent(organizationId)}`
    headers.Authorization = `Bearer ${propelAuthToken}`
    headers['Content-Type'] = 'application/json'
    body = JSON.stringify(tokenRequest)
  } else if (organizationId || userId) {
    const queryParams = new URLSearchParams()
    if (organizationId) queryParams.append('organization_id', organizationId)
    if (userId) queryParams.append('user_id', userId)
    url = `${tokenEndpoint}?${queryParams.toString()}`
  }

  const response = await fetch(url, {
    method: 'POST',
    headers,
    body,
  })

  if (!response.ok) {
    const errorText = await response.text()

    // Check for specific error conditions
    if (response.status === 401 || errorText.includes('invalid_client')) {
      // Client credentials are invalid - need to re-register
      throw new Error(
        'DCR_EXPIRED: Client registration has expired. Please try authenticating again.',
      )
    }

    if (
      errorText.includes('identity mismatch') ||
      errorText.includes('Identity mismatch')
    ) {
      // Identity mismatch - this is a serious error
      throw new Error(
        'IDENTITY_MISMATCH: Your user identity does not match the registration. Please contact support.',
      )
    }

    throw new Error(`Failed to exchange code for tokens: ${errorText}`)
  }

  return response.json()
}

/**
 * Main authentication function for MCP servers
 */
interface AuthenticateMCPServerOptions {
  forceConsentPrompt?: boolean
}

export async function authenticateMCPServer(
  server: OrganizationMCPServer,
  userId?: string,
  orgId?: string,
  propelAuthToken?: string,
  options?: AuthenticateMCPServerOptions,
): Promise<void> {
  const {
    server_url,
    server_name,
    auth_type,
    id: server_id,
  } = server.mcp_server

  try {
    // Discover OAuth metadata
    const metadata = await discoverOAuthMetadata(
      server_url,
      server_id,
      auth_type,
      orgId,
      propelAuthToken,
    )

    // Get or create client registration
    let registration: ClientRegistration
    registration = await getClientRegistration(
      metadata,
      server_name,
      server_id,
      auth_type ?? undefined,
      orgId || undefined,
      userId || undefined,
      propelAuthToken || undefined,
    )

    // Generate state for CSRF protection
    const state = crypto.randomUUID()
    sessionStorage.setItem(`oauth_state_${server_name}`, state)

    // Generate PKCE code verifier and challenge
    const codeVerifier = generateCodeVerifier()
    const codeChallenge = await generateCodeChallenge(codeVerifier)

    // Store code verifier for token exchange
    sessionStorage.setItem(`oauth_verifier_${server_name}`, codeVerifier)

    // Store org and user IDs for MITM token exchange
    if (auth_type === 'mitm') {
      if (orgId) sessionStorage.setItem(`oauth_org_${server_name}`, orgId)
      if (userId) sessionStorage.setItem(`oauth_user_${server_name}`, userId)
    }

    const authorizeRequest: OAuthAuthorizeRequest = {
      response_type: 'code',
      client_id: registration.client_id,
      redirect_uri: registration.redirect_uri,
      state: state,
      // Add specific scopes based on server
      // XX (JAL|2025.9.18): Slop detected. This doesn't seem like it should be here
      scope: server_name === 'asana' ? 'default' : 'openid profile email',
      // PKCE parameters
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    }

    if (auth_type === 'mitm' && options?.forceConsentPrompt) {
      authorizeRequest.prompt = 'consent'
    }

    const authorizeParams = toUrlSearchParams(authorizeRequest)
    if (auth_type === 'mitm') {
      if (orgId) authorizeParams.append('organization_id', orgId)
      if (userId) authorizeParams.append('user_id', userId)
    }

    let authUrl = `${metadata.authorization_endpoint}?${authorizeParams}`
    if (auth_type === 'mitm' && orgId && propelAuthToken) {
      let response = await fetch(
        `${API_URL}${metadata.authorization_endpoint}?organization_id=${encodeURIComponent(orgId)}`,
        {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${propelAuthToken}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(authorizeRequest),
        },
      )
      if (!response.ok && metadata.registration_endpoint) {
        sessionStorage.removeItem(`oauth_client_${server_name}`)
        registration = await registerClient(
          metadata.registration_endpoint,
          server_name,
          server_id,
          auth_type,
          orgId,
          userId || undefined,
          propelAuthToken,
        )
        authorizeRequest.client_id = registration.client_id
        authorizeRequest.redirect_uri = registration.redirect_uri
        response = await fetch(
          `${API_URL}${metadata.authorization_endpoint}?organization_id=${encodeURIComponent(orgId)}`,
          {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${propelAuthToken}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(authorizeRequest),
          },
        )
      }
      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Failed to build authorization URL: ${errorText}`)
      }
      const data = (await response.json()) as { authorization_url: string }
      authUrl = data.authorization_url
    }

    // Pre-flight: verify the client registration is still live on the MCP
    // server.  In-memory DCR registrations are lost when the MCP server
    // restarts, but the database still reports them as active.  A quick
    // fetch (without following the redirect) lets us detect stale
    // registrations and re-register before opening the popup.
    if (auth_type !== 'mitm') {
      try {
        const preflight = await fetch(authUrl, { redirect: 'manual' })
        if (preflight.status >= 400) {
          console.log(
            '[OAuth] Client registration stale on MCP server, re-registering…',
          )
          sessionStorage.removeItem(`oauth_client_${server_name}`)
          registration = await registerClient(
            metadata.registration_endpoint!,
            server_name,
            server_id,
            auth_type ?? undefined,
            orgId || undefined,
            userId || undefined,
            propelAuthToken || undefined,
          )
          authorizeParams.set('client_id', registration.client_id)
          authorizeParams.set('redirect_uri', registration.redirect_uri)
          authUrl = `${metadata.authorization_endpoint}?${authorizeParams}`
        }
      } catch {
        // Network / CORS error — proceed with the popup anyway
      }
    }

    // Open authorization in a popup window
    const authWindow = window.open(
      authUrl,
      'oauth',
      'width=600,height=700,left=200,top=100',
    )

    if (!authWindow) {
      throw new Error(
        'Failed to open authorization window. Please check your popup blocker.',
      )
    }

    // Listen for the callback message
    return new Promise((resolve, reject) => {
      const handleMessage = async (event: MessageEvent) => {
        // Verify origin
        if (event.origin !== window.location.origin) {
          return
        }

        if (
          event.data.type === 'oauth_callback' &&
          event.data.server === server_name
        ) {
          window.removeEventListener('message', handleMessage)

          const { code, state: returnedState, error } = event.data

          if (error) {
            console.error('OAuth callback error:', {
              error,
              errorDescription: event.data.errorDescription,
            })
            reject(
              new Error(
                `OAuth error: ${error}${event.data.errorDescription ? ` - ${event.data.errorDescription}` : ''}`,
              ),
            )
            return
          }

          // Verify state
          const savedState = sessionStorage.getItem(
            `oauth_state_${server_name}`,
          )
          if (returnedState !== savedState) {
            reject(new Error('OAuth state mismatch - possible CSRF attack'))
            return
          }

          try {
            // Get the code verifier from session storage
            const codeVerifier = sessionStorage.getItem(
              `oauth_verifier_${server_name}`,
            )

            // Get org and user IDs for MITM servers
            const organizationId = sessionStorage.getItem(
              `oauth_org_${server_name}`,
            )
            const userId = sessionStorage.getItem(`oauth_user_${server_name}`)

            // Exchange code for tokens
            let tokens
            try {
              tokens = await exchangeCodeForTokens(
                metadata.token_endpoint,
                code,
                registration,
                registration.redirect_uri,
                codeVerifier || undefined,
                organizationId || undefined,
                userId || undefined,
                server_id,
                auth_type,
                propelAuthToken,
              )
            } catch (error: any) {
              if (error.message?.startsWith('DCR_EXPIRED')) {
                // Client registration expired - automatically re-register
                console.log(
                  '[DCR] Registration expired, attempting automatic re-registration',
                )
                sessionStorage.removeItem(`oauth_client_${server_name}`)

                // Re-register the client
                try {
                  const newRegistration = await registerClient(
                    metadata.registration_endpoint!,
                    server_name,
                    server_id,
                    auth_type ?? undefined,
                    organizationId || undefined,
                    userId || undefined,
                    propelAuthToken || undefined,
                  )

                  // Try token exchange again with new registration
                  tokens = await exchangeCodeForTokens(
                    metadata.token_endpoint,
                    code,
                    newRegistration,
                    newRegistration.redirect_uri,
                    codeVerifier || undefined,
                    organizationId || undefined,
                    userId || undefined,
                    server_id,
                    auth_type,
                    propelAuthToken,
                  )

                  // Update registration reference for the success path
                  registration = newRegistration
                  console.log(
                    '[DCR] Successfully re-registered and exchanged tokens',
                  )
                } catch (retryError) {
                  console.error('[DCR] Re-registration failed:', retryError)
                  reject(
                    new Error(
                      'Authentication failed after automatic retry. Please close this window and try again.',
                    ),
                  )
                  return
                }
              } else if (error.message?.startsWith('IDENTITY_MISMATCH')) {
                // Identity mismatch - serious error, no retry
                console.error(
                  '[DCR] Identity mismatch detected - user must contact support',
                )
                reject(
                  new Error(
                    '⚠️ SECURITY ERROR: Your user identity does not match the registration. Please contact your administrator immediately. Do not attempt to retry.',
                  ),
                )
                return
              } else {
                // Other errors - pass through
                throw error
              }
            }

            const providerMetadata =
              server_name === 'salesforce'
                ? Object.fromEntries(
                    (
                      [
                        'instance_url',
                        'id',
                        'issued_at',
                        'signature',
                        'scope',
                        'token_type',
                      ] as const
                    ).flatMap((key) => {
                      const value = tokens[key]
                      return typeof value === 'string' && value
                        ? [[key, value]]
                        : []
                    }),
                  )
                : undefined

            // Send tokens to parent window to be handled by the chat component
            window.postMessage(
              {
                type: 'mcp_oauth_tokens',
                serverName: server_name,
                serverId: server.mcp_server.id,
                tokens: {
                  access_token: tokens.access_token,
                  refresh_token: tokens.refresh_token,
                  token_expiry: tokens.expires_in
                    ? Date.now() + tokens.expires_in * 1000
                    : undefined,
                  client_id: registration.client_id,
                  client_secret: registration.client_secret,
                  provider_metadata:
                    providerMetadata && Object.keys(providerMetadata).length > 0
                      ? providerMetadata
                      : undefined,
                } as TokenMessage,
              },
              window.location.origin,
            )

            // Clean up all stored OAuth data
            sessionStorage.removeItem(`oauth_state_${server_name}`)
            sessionStorage.removeItem(`oauth_verifier_${server_name}`)
            sessionStorage.removeItem(`oauth_org_${server_name}`)
            sessionStorage.removeItem(`oauth_user_${server_name}`)
            sessionStorage.removeItem(`oauth_client_${server_name}`)

            resolve()
          } catch (error) {
            reject(error)
          }
        }
      }

      window.addEventListener('message', handleMessage)

      // Clean up if window is closed
      const checkClosed = setInterval(() => {
        if (authWindow.closed) {
          clearInterval(checkClosed)
          window.removeEventListener('message', handleMessage)
          reject(new Error('Authorization window was closed'))
        }
      }, 1000)
    })
  } catch (error) {
    // Clean up any stored state on error
    sessionStorage.removeItem(`oauth_state_${server_name}`)
    sessionStorage.removeItem(`oauth_verifier_${server_name}`)
    sessionStorage.removeItem(`oauth_org_${server_name}`)
    sessionStorage.removeItem(`oauth_user_${server_name}`)
    sessionStorage.removeItem(`oauth_client_${server_name}`)
    throw error
  }
}

/**
 * Handle OAuth callback - to be used by the callback page
 */
export function handleOAuthCallback() {
  const params = new URLSearchParams(window.location.search)
  const code = params.get('code')
  const state = params.get('state')
  const error = params.get('error')
  const errorDescription = params.get('error_description')

  // Determine which server this is for based on state
  let server = ''
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i)
    if (key?.startsWith('oauth_state_')) {
      const savedState = sessionStorage.getItem(key)
      if (savedState === state) {
        server = key.replace('oauth_state_', '')
        break
      }
    }
  }

  if (window.opener && !window.opener.closed) {
    // Send message back to the opener
    window.opener.postMessage(
      {
        type: 'oauth_callback',
        server,
        code,
        state,
        error,
        errorDescription,
      },
      window.location.origin,
    )

    // Close the window after a short delay
    setTimeout(() => {
      window.close()
    }, 2000)
  } else {
    // If opener is closed, show an error
    console.error('Parent window closed. Unable to complete OAuth flow.')
  }
}
